/**
 * What is waiting to be sent: how many, whether they are on their way, and which ones need the
 * person (the server refused them, or something changed since they were made). Nothing waiting is
 * ever shown as sent.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { HoursJumpPrompt, hoursJumpOf, type HoursConfirmation } from "../logbook/HoursInput";
import { ApiError } from "../services/api-error";
import { localDay, stockholmTime } from "../logbook/time";
import { checklistAsNote, discard, exportQueue, resendWithExplanation } from "./queue-actions";
import { runSync, useAutoSync, useQueueState } from "./queue-state";
import type { QueueItem } from "./queue";

function Item({ item }: { item: QueueItem }) {
  const { t } = useTranslation();
  const when = String(item.body.occurredAt ?? item.createdAt);
  const [explaining, setExplaining] = useState(false);
  const jump = item.errorCode === "hours_jump" ? new ApiError(item.lastError ?? "", "hours_jump", item.details) : null;
  return (
    <li className="space-y-1 rounded bg-gray-50 p-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span>
          <span className="font-medium">{t(`logbook.offline.kinds.${item.kind}`)}</span> {item.label} · {localDay(when)} {stockholmTime(when)}
        </span>
        <span className={`rounded-full px-2 py-0.5 text-xs ${item.status === "needs_review" ? "bg-red-100 text-red-800" : item.status === "failed" ? "bg-amber-100 text-amber-900" : "bg-gray-200 text-gray-700"}`}>
          {t(`logbook.offline.status.${item.status}`)}
        </span>
      </div>
      {item.status === "needs_review" && (
        <div className="space-y-2">
          <p role="alert" className="text-red-700">
            {item.lastError}
          </p>
          {jump && hoursJumpOf(jump) && explaining && <HoursJumpPrompt error={jump} onChange={() => setExplaining(false)} onConfirm={(c: HoursConfirmation) => void resendWithExplanation(item, c)} />}
          <div className="flex flex-wrap gap-2">
            {jump && !explaining && (
              <button className="btn btn-secondary" onClick={() => setExplaining(true)}>
                {t("logbook.offline.explain")}
              </button>
            )}
            {item.errorCode === "revision_changed" && (
              <button className="btn btn-secondary" onClick={() => void checklistAsNote(item)}>
                {t("logbook.offline.keepAsNote")}
              </button>
            )}
            <button
              className="btn btn-secondary"
              onClick={() => {
                if (window.confirm(t("logbook.offline.confirmDiscard"))) void discard(item.operationId);
              }}
            >
              {t("logbook.offline.discard")}
            </button>
          </div>
        </div>
      )}
      {item.status === "failed" && item.lastError && <p className="text-xs text-amber-900">{t("logbook.offline.retrying", { error: item.lastError })}</p>}
    </li>
  );
}

export default function QueueBar() {
  const { t } = useTranslation();
  useAutoSync();
  const { items, others, notice, syncing, last } = useQueueState();
  const review = items.filter((i) => i.status === "needs_review").length;
  if (items.length === 0 && others === 0 && !notice) return null;
  return (
    <div className="space-y-2 border-b border-gray-200 bg-white px-4 py-2 text-sm" aria-label={t("logbook.offline.queueTitle")}>
      {notice && (
        <p role="status" className="font-medium text-green-800">
          {t("logbook.offline.savedOnDevice")}
        </p>
      )}
      {items.length > 0 && (
        <details open={review > 0}>
          <summary className="flex cursor-pointer flex-wrap items-center justify-between gap-2">
            <span className="font-medium">
              {t("logbook.offline.waiting", { count: items.length })}
              {review > 0 && <span className="ml-2 rounded bg-red-100 px-2 py-0.5 text-xs text-red-800">{t("logbook.offline.needsAction", { count: review })}</span>}
            </span>
            <span className="flex gap-2">
              <button className="btn btn-secondary" disabled={syncing} onClick={(e) => { e.preventDefault(); void runSync(); }}>
                {syncing ? "…" : t("logbook.offline.syncNow")}
              </button>
              <button className="btn btn-secondary" onClick={(e) => { e.preventDefault(); void exportQueue(); }}>
                {t("logbook.offline.export")}
              </button>
            </span>
          </summary>
          <ul className="mt-2 space-y-2">
            {items.map((i) => (
              <Item key={i.operationId} item={i} />
            ))}
          </ul>
          {last?.stopped === "offline" && <p className="mt-1 text-xs text-gray-600">{t("logbook.offline.stoppedOffline")}</p>}
          {last?.stopped === "session" && <p className="mt-1 text-xs text-amber-900">{t("logbook.offline.stoppedSession")}</p>}
        </details>
      )}
      {others > 0 && (
        <p role="alert" className="rounded bg-amber-50 p-2 text-amber-950">
          {t("logbook.offline.otherUser", { count: others })}{" "}
          <button className="underline" onClick={() => void exportQueue()}>
            {t("logbook.offline.export")}
          </button>
        </p>
      )}
    </div>
  );
}
